import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Loader2, Mail, CheckCircle2, Clock } from 'lucide-react';

const COOLDOWN_KEY = 'creataby_login_cooldown';
const DEFAULT_COOLDOWN_SEC = 60; // Supabase OTP default: 1 req/60s per email

// localStorage helpers — keyed by lowercased email
const getCooldownExpiry = (email) => {
  try {
    const raw = localStorage.getItem(COOLDOWN_KEY);
    if (!raw) return 0;
    const map = JSON.parse(raw);
    return map[email.toLowerCase()] || 0;
  } catch {
    return 0;
  }
};

const setCooldownExpiry = (email, expiresAtMs) => {
  try {
    const raw = localStorage.getItem(COOLDOWN_KEY);
    const map = raw ? JSON.parse(raw) : {};
    map[email.toLowerCase()] = expiresAtMs;
    // Prune expired entries so the map doesn't grow unbounded
    const now = Date.now();
    Object.keys(map).forEach((k) => { if (map[k] < now) delete map[k]; });
    localStorage.setItem(COOLDOWN_KEY, JSON.stringify(map));
  } catch {
    /* ignore quota errors */
  }
};

const Login = () => {
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState('idle');
  const [errorMessage, setErrorMessage] = useState('');
  const [accessCode, setAccessCode] = useState('');
  const [isWebinar, setIsWebinar] = useState(false);
  const [cooldownRemaining, setCooldownRemaining] = useState(0);
  const { signInWithMagicLink } = useAuth();
  const cooldownTimerRef = useRef(null);

  // Store referral source (e.g., ?ref=webinar) for AWeber tagging
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const ref = params.get('ref');
    if (ref) {
      sessionStorage.setItem('signup_ref', ref);
      if (ref === 'webinar') {
        setIsWebinar(true);
      }
    }
  }, []);

  // Whenever the email field changes, sync the cooldown timer for THAT email.
  useEffect(() => {
    if (cooldownTimerRef.current) clearInterval(cooldownTimerRef.current);

    const trimmed = email.trim();
    if (!trimmed) {
      setCooldownRemaining(0);
      return;
    }
    const expiresAt = getCooldownExpiry(trimmed);
    const tick = () => {
      const rem = Math.max(0, Math.ceil((expiresAt - Date.now()) / 1000));
      setCooldownRemaining(rem);
      if (rem <= 0 && cooldownTimerRef.current) {
        clearInterval(cooldownTimerRef.current);
        cooldownTimerRef.current = null;
      }
    };
    tick();
    if (expiresAt > Date.now()) {
      cooldownTimerRef.current = setInterval(tick, 1000);
    }
    return () => {
      if (cooldownTimerRef.current) clearInterval(cooldownTimerRef.current);
    };
  }, [email]);

  const isValidEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email.trim());
  };

  const canSubmit = (
    email.trim() &&
    isValidEmail(email) &&
    status !== 'sending' &&
    cooldownRemaining === 0 &&
    (!isWebinar || accessCode.trim())
  );

  const startCooldown = (emailToCool, seconds = DEFAULT_COOLDOWN_SEC) => {
    const expiresAt = Date.now() + seconds * 1000;
    setCooldownExpiry(emailToCool, expiresAt);
    setCooldownRemaining(seconds);
    if (cooldownTimerRef.current) clearInterval(cooldownTimerRef.current);
    cooldownTimerRef.current = setInterval(() => {
      const rem = Math.max(0, Math.ceil((expiresAt - Date.now()) / 1000));
      setCooldownRemaining(rem);
      if (rem <= 0) {
        clearInterval(cooldownTimerRef.current);
        cooldownTimerRef.current = null;
      }
    }, 1000);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;

    // Validate webinar access code
    if (isWebinar && accessCode.trim().toLowerCase() !== 'webinar') {
      setStatus('error');
      setErrorMessage('Invalid access code. Please check your webinar materials.');
      return;
    }

    if (isWebinar) {
      sessionStorage.setItem('webinar_access_code', accessCode.trim().toLowerCase());
    }

    setStatus('sending');
    setErrorMessage('');

    const { error } = await signInWithMagicLink(email);

    if (error) {
      setStatus('error');
      let errorMsg = error.message || 'Failed to send magic link. Please try again.';
      const lowerMsg = errorMsg.toLowerCase();
      const code = String(error.code || '').toLowerCase();
      // Detect ALL known Supabase rate-limit shapes: 429 status, per-email
      // "over_email_send_rate_limit", generic "over_request_rate_limit",
      // and the human-readable messages Supabase sends.
      const isRate = (
        error.status === 429 ||
        code.includes('over_email_send_rate_limit') ||
        code.includes('over_request_rate_limit') ||
        code.includes('rate_limit') ||
        errorMsg.includes('429') ||
        lowerMsg.includes('rate limit') ||
        lowerMsg.includes('too many') ||
        lowerMsg.includes('for security purposes') ||
        lowerMsg.includes('only request this after') ||
        lowerMsg.includes('email rate limit')
      );
      if (isRate) {
        // Parse "after N seconds" from Supabase's message when available;
        // otherwise fall back to 60s.
        let cooldownSec = DEFAULT_COOLDOWN_SEC;
        const match = errorMsg.match(/after\s+(\d+)\s+seconds?/i);
        if (match) {
          const parsed = parseInt(match[1], 10);
          if (Number.isFinite(parsed) && parsed > 0 && parsed <= 3600) {
            cooldownSec = parsed;
          }
        }
        startCooldown(email, cooldownSec);
        errorMsg = `Too many sign-in requests for this email. Please wait for the countdown below before trying again.`;
      } else if (error.message?.includes('not authorized') || error.message?.includes('403')) {
        errorMsg = 'Email authentication is not enabled. Please contact support.';
      } else if (error.status === 400 || error.message?.includes('400')) {
        errorMsg = 'Unable to send magic link. Please check your email and try again.';
      } else if (error.message?.includes('network') || error.message?.includes('fetch')) {
        errorMsg = 'Network error. Please check your connection and try again.';
      }
      setErrorMessage(errorMsg);
    } else {
      // Success — start the cooldown so the user can't immediately re-request
      startCooldown(email, DEFAULT_COOLDOWN_SEC);
      setStatus('sent');
    }
  };

  const handleResend = () => {
    setStatus('idle');
  };

  return (
    <div className="min-h-screen flex flex-col lg:flex-row">
      {/* Left: Creataby splash graphic panel — 52% on desktop, hidden on small screens */}
      <div
        className="hidden lg:flex lg:w-[52%] items-center justify-center min-h-screen"
        style={{ backgroundColor: '#03183D' }}
        data-testid="login-splash-panel"
      >
        <img
          src="/creataby-splash.webp"
          alt="Creataby — Turn what you know into something you can sell"
          className="w-full h-full max-h-screen"
          style={{ objectFit: 'contain', objectPosition: 'center' }}
          data-testid="login-splash-image"
        />
      </div>

      {/* Right: Form panel — 48% on desktop, full width on mobile */}
      <div
        className="flex-1 lg:w-[48%] flex items-center justify-center p-6 sm:p-10 lg:p-16 min-h-screen"
        style={{ backgroundColor: '#F7F9FC' }}
        data-testid="login-form-panel"
      >
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
          className="w-full"
          style={{ maxWidth: '440px' }}
        >
          <AnimatePresence mode="wait">
            {status === 'sent' ? (
              <motion.div
                key="sent"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                transition={{ duration: 0.3 }}
                className="text-center"
              >
                <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-6" style={{ backgroundColor: 'rgba(22,170,168,0.15)' }}>
                  <CheckCircle2 className="w-8 h-8" style={{ color: '#16AAA8' }} />
                </div>
                <h1 className="text-2xl sm:text-3xl font-bold tracking-tight mb-3" style={{ color: '#0A2E5D' }}>
                  Check your email
                </h1>
                <p className="text-slate-600 mb-2">
                  We sent a magic link to
                </p>
                <p className="font-medium mb-8" style={{ color: '#16AAA8' }}>{email}</p>
                <p className="text-sm text-slate-600 mb-6">
                  Click the link in your email to sign in. The link will expire in 1 hour.
                </p>
                <Button
                  variant="outline"
                  onClick={handleResend}
                  disabled={cooldownRemaining > 0}
                  className="w-full border-slate-300 hover:bg-slate-100"
                  data-testid="resend-link-btn"
                >
                  {cooldownRemaining > 0 ? `Resend available in ${cooldownRemaining}s` : 'Resend link'}
                </Button>
              </motion.div>
            ) : (
              <motion.div
                key="form"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                transition={{ duration: 0.3 }}
              >
                {/* Mobile-only compact logo + tagline */}
                <div className="lg:hidden mb-6 flex flex-col items-center text-center">
                  <img
                    src="/banner-logo.png"
                    alt="Creataby"
                    className="h-14 mb-3"
                    data-testid="login-logo-mobile"
                  />
                  <p className="text-sm" style={{ color: '#0A2E5D' }} data-testid="login-mobile-tagline">
                    From rough idea to ready-to-sell product.
                  </p>
                </div>

                <h1
                  className="text-3xl sm:text-4xl font-bold tracking-tight mb-3"
                  style={{ color: '#0A2E5D' }}
                  data-testid="login-heading"
                >
                  Welcome back
                </h1>
                <p className="text-slate-600 mb-8" data-testid="login-subheading">
                  Enter your email and we&rsquo;ll send you a secure sign-in link.
                </p>

                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="space-y-2">
                    <Label htmlFor="email" className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500">
                      Email Address
                    </Label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
                      <Input
                        id="email"
                        type="email"
                        placeholder="you@example.com"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        className="pl-10 h-12 bg-white border-slate-300 focus:ring-2 focus:ring-teal-500/30 focus:border-teal-500"
                        disabled={status === 'sending'}
                        required
                        data-testid="login-email-input"
                      />
                    </div>
                  </div>

                  {/* Access code field for webinar users */}
                  {isWebinar && (
                    <div className="space-y-2">
                      <Label htmlFor="access-code" className="text-xs uppercase tracking-wider font-semibold text-slate-500">
                        Access Code
                      </Label>
                      <Input
                        id="access-code"
                        type="text"
                        placeholder="Enter your webinar access code"
                        value={accessCode}
                        onChange={(e) => setAccessCode(e.target.value)}
                        className="h-12 bg-white border-slate-300 focus:ring-2 focus:ring-teal-500/30 focus:border-teal-500"
                        disabled={status === 'sending'}
                        required
                        data-testid="login-access-code-input"
                      />
                    </div>
                  )}

                  {status === 'error' && (
                    <motion.p
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      className="text-red-600 text-sm"
                      data-testid="login-error-message"
                    >
                      {errorMessage}
                    </motion.p>
                  )}

                  <div>
                    <Button
                      type="submit"
                      className="w-full text-white font-semibold transition-all duration-200 active:scale-[0.98] hover:brightness-110"
                      style={{ backgroundColor: '#16AAA8', height: '52px' }}
                      disabled={!canSubmit}
                      data-testid="submit-magic-link-btn"
                    >
                      {status === 'sending' ? (
                        <>
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                          Sending link...
                        </>
                      ) : cooldownRemaining > 0 ? (
                        <>
                          <Clock className="mr-2 h-4 w-4" />
                          Try again in {cooldownRemaining}s
                        </>
                      ) : (
                        <>
                          <Mail className="mr-2 h-4 w-4" />
                          Email My Sign-In Link
                        </>
                      )}
                    </Button>
                    <p className="text-xs text-slate-500 text-center mt-3" data-testid="login-helper-text">
                      {cooldownRemaining > 0 ? (
                        <span data-testid="cooldown-helper">
                          A sign-in link was just requested for this email. Please check your inbox — you can request another in {cooldownRemaining} second{cooldownRemaining === 1 ? '' : 's'}.
                        </span>
                      ) : (
                        'No password needed. The link should arrive within a few moments.'
                      )}
                    </p>
                  </div>
                </form>

                <div className="mt-8 pt-6 border-t border-slate-200 space-y-2 text-center">
                  <p className="text-xs text-slate-500">
                    By continuing, you agree to our{' '}
                    <a href="/terms" className="underline hover:text-slate-700">Terms of Service</a>
                    {' '}and{' '}
                    <a href="/privacy" className="underline hover:text-slate-700">Privacy Policy</a>
                  </p>
                  <p className="text-xs text-slate-500" data-testid="login-support-link">
                    Having trouble signing in?{' '}
                    <a
                      href="mailto:support@creataby.com"
                      className="underline font-medium hover:text-slate-700"
                      style={{ color: '#0A2E5D' }}
                      data-testid="login-support-mailto"
                    >
                      Contact support
                    </a>
                  </p>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </motion.div>
      </div>
    </div>
  );
};

export default Login;
